/* eslint-disable react/no-multi-comp -- allow subrouting components */
import { CompassOutlined } from "@ant-design/icons";
import { Result, Spin } from "antd";
import { Suspense, lazy } from "react";
import { DataRouteObject, Navigate, Outlet, useRouteError } from "react-router";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import AppLayout from "./AppLayout";
import HomeButton from "./components/UI/HomeButton";
import RetryButton from "./components/UI/RetryButton";

// components for the different urls of the app
const TendersPage = lazy(
  () => import("@/components/Pages/Tenders/TendersPage"),
);
const TenderPage = lazy(() => import("@/components/Pages/Tender/TenderPage"));
const ManageRequirements = lazy(
  () => import("@/components/Pages/Tender/ManageRequirements"),
);
const ManageOffers = lazy(
  () => import("@/components/Pages/Tender/ManageOffers"),
);
const ManageTenderRequirements = lazy(
  () => import("@/components/Pages/Tender/ManageTenderRequirements"),
);
const TenderRequirementValidationPage = lazy(
  () =>
    import(
      "@/components/Pages/TenderRequirementValidation/TenderRequirementValidationPage"
    ),
);
const StaticQueryOutput = lazy(
  () =>
    import("@/components/Pages/TenderRequirementValidation/StaticQueryOutput"),
);
const DynamicQueries = lazy(
  () => import("@/components/Pages/TenderRequirementValidation/DynamicQueries"),
);

const DynamicQueryOutput = lazy(
  () =>
    import("@/components/Pages/TenderRequirementValidation/DynamicQueryOutput"),
);

function LoadingSpin() {
  return (
    <Spin
      spinning
      style={{ textAlign: "center", width: "100%", marginTop: "100px" }}
    />
  );
}

export function NotFound() {
  return (
    <Result
      status="404"
      title="404"
      subTitle="Sorry, the page you visited does not exist."
      extra={<HomeButton />}
    />
  );
}

export function ErrorBoundary() {
  const error: unknown = useRouteError();
  return (
    <Result
      status="500"
      title={error?.toString()}
      subTitle="Sorry, something went wrong"
      extra={<RetryButton />}
    />
  );
}

export const routes: DataRouteObject[] = [
  {
    id: "root",
    path: "/",
    element: (
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <AppLayout />
      </QueryParamProvider>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        id: "home",
        path: "/",
        index: true,
        element: <Navigate to="/tenders" />,
        errorElement: <ErrorBoundary />,
      },
      {
        id: "tenders",
        path: "/tenders",
        element: (
          <Suspense fallback={<LoadingSpin />}>
            <TendersPage />
          </Suspense>
        ),
        handle: {
          title: "Tenders",
          icon: CompassOutlined,
        },
      },
      {
        id: "tender_details",
        path: "/tenders/:tenderId",
        element: (
          <Suspense fallback={<LoadingSpin />}>
            <TenderPage />
          </Suspense>
        ),
        handle: {
          title: "Tender details",
          icon: CompassOutlined,
        },
        children: [
          {
            id: "requirements",
            path: "requirements",
            element: (
              <Suspense fallback={<LoadingSpin />}>
                <ManageRequirements />
              </Suspense>
            ),
          },
          {
            id: "offers",
            path: "offers",
            element: (
              <Suspense fallback={<LoadingSpin />}>
                <ManageOffers />
              </Suspense>
            ),
          },
          {
            id: "awarding",
            path: "awarding",
            element: (
              <Suspense fallback={<LoadingSpin />}>
                <ManageTenderRequirements />
              </Suspense>
            ),
          },
        ],
      },
      {
        id: "tender_requirement_validation",
        path: "/tenders/:tenderId/requirement_validation/:requirementId/offer/:offerId",
        element: (
          <Suspense fallback={<LoadingSpin />}>
            <TenderRequirementValidationPage />
          </Suspense>
        ),
        handle: {
          title: "Requirement validation",
          icon: CompassOutlined,
        },
        children: [
          {
            id: "llm_output",
            index: true,
            element: (
              <Suspense fallback={<LoadingSpin />}>
                <StaticQueryOutput />
              </Suspense>
            ),
          },
          {
            id: "prompts",
            path: "prompts",
            element: <Outlet />,
            children: [
              {
                index: true,
                id: "prompts_list",
                element: (
                  <Suspense fallback={<LoadingSpin />}>
                    <DynamicQueries />
                  </Suspense>
                ),
              },
              {
                id: "prompt_output",
                path: ":promptId",
                element: (
                  <Suspense fallback={<LoadingSpin />}>
                    <DynamicQueryOutput />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        id: "not-found",
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

const appendChildren = <R extends typeof routes>(
  routes: R,
): DataRouteObject[] =>
  routes
    .map((route) => (route?.children ? appendChildren(route.children) : route))
    .flat();

export const navigationMenuRoutes = appendChildren(routes)
  .filter((route) => !!route.handle?.icon)
  .map((route) => ({
    id: route.id,
    path: route.path,
    title: route.handle?.title,
    icon: route.handle?.icon,
    disabled: route.handle?.disabled,
  }));
